import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Container,
  alpha,
  Button,
  Grid,
  Avatar,
  Paper,
  Skeleton,
  Fab,
  Divider,
  useTheme,
  styled,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import QuizIcon from "@mui/icons-material/Quiz";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import MyContentStats from "./MyContentStats";
import CheckIcon from "@mui/icons-material/Check";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { Pagination } from "@mui/material";

import {
  lessonMapping,
  getLessonTypeParam,
  lessonUrlMapping,
} from "./functions";
import ChatIcon from "@mui/icons-material/Chat";
import QuizResultsTooltip from "./QuizResultsTooltip";
import FlashcardResultsTooltip from "./FlashcardResultsTooltip"; // Adjust path as needed

import {
  collection,
  query,
  where,
  getDocs,
  limit,
  orderBy,
} from "firebase/firestore";
import { db } from "./firebase";
import { formatDate, createUrlSlug, availableSpeakers } from "./functions";
import { useNavigate } from "react-router-dom";

import {
  typeStyles,
  TabButton,
  ContentCard,
  CardContent,
  TypeBadge,
  InfoBadge,
  ActionBox,
  getIconColor,
  getGradeColor,
  getTypeName,
} from "./MyContentStyles";

const MyContent = ({ user, styles, reloadTrigger, role }) => {
  const [activeFilter, setActiveFilter] = useState("all");
  const [flashcards, setFlashcards] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [essays, setEssays] = useState([]);
  const [chats, setChats] = useState([]);

  const [assistentChats, setAssistentChats] = useState([]);
  // Add these state variables at the beginning of your component
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(3);

  // Add this function to handle page changes
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  const [podcasts, setPodcasts] = useState([]);
  // Update loading state

  const [hpTests, setHpTests] = useState([]);
  const [loading, setLoading] = useState({
    flashcards: true,
    quizzes: true,
    podcasts: true,
    essays: true,
    lessons: true,
    chats: true,
    assistentChats: true,
    hpTests: true, // Add this
    all: true,
  });

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Add this effect to update itemsPerPage when the active filter changes
  useEffect(() => {
    setItemsPerPage(activeFilter === "all" ? 6 : 3);
  }, [activeFilter]);

  // Add this to the useEffect that calls other fetch functions
  useEffect(() => {
    if (user?.uid) {
      fetchUserFlashcards();
      fetchUserQuizzes();
      fetchUserPodcasts();
      fetchUserEssays();
      fetchUserLessons();
      fetchUserChats();
      fetchUserAssistentChats(); // Add this line
      fetchUserHpTests();
    }
  }, [user?.uid, reloadTrigger]);

  useEffect(() => {
    if (
      !loading.flashcards &&
      !loading.quizzes &&
      !loading.podcasts &&
      !loading.essays &&
      !loading.lessons &&
      !loading.chats &&
      !loading.assistentChats &&
      !loading.hpTests // Add this
    ) {
      setLoading((prev) => ({ ...prev, all: false }));
    }
  }, [
    loading.flashcards,
    loading.quizzes,
    loading.podcasts,
    loading.essays,
    loading.lessons,
    loading.chats,
    loading.assistentChats,
    loading.hpTests, // Add this
  ]);

  const fetchUserHpTests = async () => {
    setLoading((prev) => ({ ...prev, hpTests: true, all: true }));
    try {
      const q = query(
        collection(db, "hpTestProgress"),
        where("userId", "==", user.uid),
        limit(9),
      );
      const querySnapshot = await getDocs(q);
      const hpTestItems = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          testId: data.testId,
          title: data.testName || "Okänt test",
          date: formatDate(data.lastUpdated || Date.now()),
          testInProgress: data.testInProgress || false,
          answeredQuestionsCount: data.results ? data.results.length : 0,
          totalQuestions: data.totalQuestions || 0,
          timeLeft: data.timeLeft || 0,
          results: data.results || [],
          type: "hpTests",
          createdAt: data.lastUpdated || 0,
        };
      });
      setHpTests(hpTestItems);
    } catch (error) {
      console.error("Error fetching HP tests:", error);
    } finally {
      setLoading((prev) => ({ ...prev, hpTests: false }));
    }
  };

  // For fetchUserAssistentChats
  const fetchUserAssistentChats = async () => {
    setLoading((prev) => ({ ...prev, assistentChats: true, all: true }));
    try {
      // First, get all the user's assistants/teachers
      const teachersCollection = collection(db, "teachers");
      const teachersQuery = query(
        teachersCollection,
        where("userId", "==", user.uid),
      );

      const teachersSnapshot = await getDocs(teachersQuery);
      const teachersList = teachersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Use a single query to get the latest message for each assistant
      // This is more efficient than querying for each assistant individually
      const conversationsRef = collection(db, "conversations");
      const latestMessagesQuery = query(
        conversationsRef,
        where("userId", "==", user.uid),
        where(
          "conversationType",
          "in",
          teachersList.map((t) => t.id),
        ),
        orderBy("createdAt", "desc"),
      );

      const messagesSnapshot = await getDocs(latestMessagesQuery);

      // Group messages by conversationType (teacher/assistant ID)
      const latestMessagesByTeacher = {};
      messagesSnapshot.docs.forEach((doc) => {
        const data = doc.data();
        const teacherId = data.conversationType;

        if (
          !latestMessagesByTeacher[teacherId] ||
          data.createdAt > latestMessagesByTeacher[teacherId].createdAt
        ) {
          latestMessagesByTeacher[teacherId] = data;
        }
      });

      // Create the final list with the latest message information
      const assistentChatsList = teachersList.map((teacher) => {
        const latestMessage = latestMessagesByTeacher[teacher.id];

        return {
          id: teacher.id,
          title: teacher.characterName || "Unnamed Assistant",
          date: latestMessage
            ? formatDate(latestMessage.createdAt)
            : formatDate(teacher.createdAt || Date.now()),
          category: teacher.category || "Assistant",
          slug: createUrlSlug(teacher.characterName || "unnamed-assistant"),
          imageUrl: teacher.imageUrl,
          type: "assistentChats",
          lastMessageContent: latestMessage?.message,

          lastMessage: latestMessage?.message,
          createdAt: latestMessage?.createdAt ||
            teacher.createdAt || { seconds: 0 },
        };
      });

      // Sort by most recent message
      assistentChatsList.sort((a, b) => {
        const aTimestamp = a.createdAt.seconds || 0;
        const bTimestamp = b.createdAt.seconds || 0;
        return bTimestamp - aTimestamp;
      });

      setAssistentChats(assistentChatsList);
    } catch (error) {
      console.error("Error fetching assistant chats:", error);
    } finally {
      setLoading((prev) => ({ ...prev, assistentChats: false }));
    }
  };

  const fetchUserChats = async () => {
    setLoading((prev) => ({ ...prev, chats: true, all: true }));
    try {
      const chatsRef = collection(db, "chats");
      const chatsQuery = query(
        chatsRef,
        where("userId", "==", user.uid),
        where("conversationType", "==", "chat"),
        orderBy("lastMessageSent", "desc"), // Sort by lastMessageSent instead of createdAt
        limit(6),
      );

      const querySnapshot = await getDocs(chatsQuery);
      const chatItems = querySnapshot.docs.map((doc) => {
        const data = doc.data();

        return {
          id: doc.id,
          title: data.name || "Untitled Chat",
          date: formatDate(data.lastMessageSent || data.createdAt), // Use lastMessageSent if available
          category: "AI Chat",
          slug: createUrlSlug(data.name || "untitled"),
          firstMessageContent: data.firstMessageContent,
          lastMessageContent: data.lastMessageContent, // Can display preview if needed
          createdAt: data.lastMessageSent || data.createdAt, // For sorting
          type: "chats",
        };
      });

      setChats(chatItems);
    } catch (error) {
      console.error("Error fetching chats:", error);
    } finally {
      setLoading((prev) => ({ ...prev, chats: false }));
    }
  };

  const fetchUserLessons = async () => {
    setLoading((prev) => ({ ...prev, lessons: true, all: true }));
    try {
      const lessonsRef = collection(db, "lesson_usage");
      const regularQuery = query(
        lessonsRef,
        where("userId", "==", user.uid),
        where("hasPrinted", "==", true),
        orderBy("timestamp", "desc"),
        limit(6),
      );

      const querySnapshot = await getDocs(regularQuery);
      const lessonItems = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.lessonName || "Untitled Lesson",
          date: formatDate(data.timestamp || Date.now()),
          category: lessonMapping(data.lessonType) || "Lesson",
          slug: createUrlSlug(data.lessonName || "untitled"),
          createdAt: data.timestamp || 0,
          type: "lessons",
          lessonType: data.lessonType,
          tags: data.tags || [],
        };
      });

      setLessons(lessonItems);
    } catch (error) {
      console.error("Error fetching lessons:", error);
    } finally {
      setLoading((prev) => ({ ...prev, lessons: false }));
    }
  };

  const fetchUserEssays = async () => {
    setLoading((prev) => ({ ...prev, essays: true, all: true }));
    try {
      const essaysQuery = query(
        collection(db, "essays"),
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(6),
      );

      const querySnapshot = await getDocs(essaysQuery);
      const essayItems = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.fileName || "Untitled Essay",
          date: formatDate(data.createdAt || Date.now()),
          category: data.rubricName || "Essay",
          slug: createUrlSlug(data.fileName || "untitled"),
          createdAt: data.createdAt || 0,
          type: "assessments",
          grade: data.scores?.overallScore?.grade || null,
        };
      });

      setEssays(essayItems);
    } catch (error) {
      console.error("Error fetching essays:", error);
    } finally {
      setLoading((prev) => ({ ...prev, essays: false }));
    }
  };

  const fetchUserFlashcards = async () => {
    setLoading((prev) => ({ ...prev, flashcards: true, all: true }));
    try {
      const flashcardsQuery = query(
        collection(db, "flashcardCollections"),
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(6),
      );

      const querySnapshot = await getDocs(flashcardsQuery);
      const flashcardItems = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data.collectionName || "Untitled Collection",
          date: formatDate(data.createdAt || Date.now()),
          cards: data.flashcards?.length || 0,
          category: data.category || "General",
          slug: createUrlSlug(data.collectionName || "untitled"),
          flashcards: data.flashcards || [],
          type: "flashcards",
          createdAt: data.createdAt,
        };
      });

      setFlashcards(flashcardItems);
    } catch (error) {
      console.error("Error fetching flashcards:", error);
    } finally {
      setLoading((prev) => ({ ...prev, flashcards: false }));
    }
  };

  const fetchUserPodcasts = async () => {
    setLoading((prev) => ({ ...prev, podcasts: true, all: true }));
    try {
      const podcastsQuery = query(
        collection(db, "podcasts"),
        where("userId", "==", user.uid),
        orderBy("createdAt", "desc"),
        limit(6),
      );

      const querySnapshot = await getDocs(podcastsQuery);
      const podcastItems = querySnapshot.docs
        .filter((doc) => {
          const data = doc.data();
          return !data.hasOwnProperty("shared") || data.shared !== true;
        })
        .map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            title: data.podcastName || "Untitled Podcast",
            date: formatDate(data.createdAt || Date.now()),
            speaker1: data.speaker1,
            speaker2: data.speaker2,
            category: "podcast",
            slug: createUrlSlug(data.podcastName || "untitled"),
            createdAt: data.createdAt || 0,
            type: "podcasts",
          };
        });

      setPodcasts(podcastItems);
    } catch (error) {
      console.error("Error fetching podcasts:", error);
    } finally {
      setLoading((prev) => ({ ...prev, podcasts: false }));
    }
  };

  const fetchUserQuizzes = async () => {
    setLoading((prev) => ({ ...prev, quizzes: true, all: true }));
    try {
      const quizzesQuery = query(
        collection(db, "quiz"),
        where("userId", "==", user.uid),
      );

      const querySnapshot = await getDocs(quizzesQuery);

      const quizItems = querySnapshot.docs
        .map((doc) => {
          const data = doc.data();

          // Handle different date formats
          let createdAtDate = data.createdAt;
          if (typeof data.createdAt === "string") {
            createdAtDate = new Date(data.createdAt);
          }

          return {
            id: doc.id,
            title: data.quizName || "Untitled Quiz",
            date: formatDate(createdAtDate),
            questions: data.results?.length || 0,
            category: data.category || "General",
            results: data.results || [],
            slug: createUrlSlug(data.quizName || "untitled"),
            createdAt: createdAtDate,
            type: "quizzes",
          };
        })
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
        .slice(0, 6);

      setQuizzes(quizItems);
    } catch (error) {
      console.error("Error fetching quizzes:", error);
    } finally {
      setLoading((prev) => ({ ...prev, quizzes: false }));
    }
  };

  // Update the allItems array
  // Update the allItems array
  const allItems = [
    ...(role === "teacher" ? lessons : []),
    ...quizzes,
    ...flashcards,

    ...podcasts,
    ...essays,

    ...chats,
    ...assistentChats,
    ...(role === "student" ? hpTests : []),
  ].sort((a, b) => {
    // Handle different date formats for sorting
    const getTimestamp = (item) => {
      if (!item.createdAt) return 0;

      // Handle Firestore timestamp objects
      if (item.createdAt.seconds) return item.createdAt.seconds;

      // Handle Date objects
      if (item.createdAt instanceof Date)
        return item.createdAt.getTime() / 1000;

      // Handle numeric timestamps
      if (typeof item.createdAt === "number") return item.createdAt / 1000;

      return 0;
    };

    return getTimestamp(b) - getTimestamp(a);
  });

  const filteredItems =
    activeFilter === "all"
      ? allItems
      : allItems.filter((item) => item.type === activeFilter);

  // Apply pagination to all tabs including "all"
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Calculate total pages for pagination controls
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

  const getSpeakerAvatar = (speakerId) => {
    const speaker = availableSpeakers.find(
      (speaker) => speaker.id === speakerId,
    );
    return speaker ? speaker.avatar : null;
  };

  const handleItemClick = (item) => {
    // Navigate based on content type
    switch (item.type) {
      case "flashcards":
        navigate(`/app/flashcards/${item.id}/${item.slug}`);
        break;
      case "quizzes":
        navigate(`/app/quiz/${item.id}/${item.slug}`);
        break;
      case "podcasts":
        navigate(`/app/podcast/${item.id}`);
        break;
      case "hpTests":
        navigate(`/app/hp/${item.testId}`);
        break;
      case "assessments":
        navigate(`/app/uppsats/${item.id}`);
        break;
      // Add this case to the switch statement in handleItemClick
      case "assistentChats":
        navigate(`/app/chat/${item.id}`);
        break;
      // Inside handleItemClick function
      case "lessons":
        navigate(
          `/app${item.lessonType !== "PowerPointGenerator.js" ? "/lektioner" : ""}/${lessonUrlMapping(item.lessonType)}/${item.id.substring(0, 10)}${getLessonTypeParam(item.lessonType) === "default" ? "" : `?type=${getLessonTypeParam(item.lessonType)}`}`,
        );
        break;
      case "chats":
        navigate(`/app/fraga-ai/${item.id}`);
        break;
      default:
        navigate(`/app/${item.type}/${item.id}`);
    }
  };

  const handleCreateNew = () => {
    // Create new content based on active filter
    switch (activeFilter) {
      case "flashcards":
        navigate("/app/flashcards");
        break;
      case "quizzes":
        navigate("/app/quiz");
        break;
      // Inside handleCreateNew
      case "podcasts":
        navigate("/app/podcast");
        break;
      case "assistentChats":
        navigate("/app/larare");
        break;
      case "lessons":
        navigate("/app/lektioner");
        break;
      case "assessments":
        navigate("/app/uppsats/ny");
        break;
      case "hpTests":
        navigate("/app/hogskoleprov");
        break;
      case "chats":
        navigate("/app/fraga-ai");
        break;
      default:
        navigate(`/app/${activeFilter}/create`);
    }
  };

  const handleShowAll = () => {
    switch (activeFilter) {
      case "flashcards":
        navigate("/app/mina-flashcards");
        break;
      case "quizzes":
        navigate("/app/mina-quiz");
        break;

      case "podcasts":
        navigate("/app/mina-podcasts");
        break;

      case "lessons":
        navigate("/app/delade-lektioner");
        break;
      case "assessments":
        navigate("/app/uppsatser");
        break;
      default:
        navigate(`/app/${activeFilter}/create`);
    }
  };

  const getTypeName = (type) => {
    switch (type) {
      case "flashcards":
        return "Flashcards";
      case "quizzes":
        return "Quiz";
      case "podcasts":
        return "Podcasts";
      case "lessons":
        return "Lektioner";
      case "assessments":
        return "Bedömningar";
      case "chats":
        return "Chattar";
      case "hpTests":
        return "Högskoleprov";
      case "assistentChats":
        return "Assistenter";
      default:
        return "Innehåll";
    }
  };

  const handleCreateNewButtonText = (type) => {
    switch (type) {
      case "flashcards":
        return "Nya flashcards";
      case "quizzes":
        return "Nytt quiz";
      case "podcasts":
        return "Ny Podcast";
      case "lessons":
        return "Ny lektion";
      case "assessments":
        return "Ny bedömning";
      case "chats":
        return "Ny chat";
      case "hpTests":
        return "Visa högskoleproven";
      case "assistentChats":
        return "Ny assistent";
      default:
        return "Innehåll";
    }
  };

  const getIconColor = (theme, colorstyle) => {
    return theme.palette.mode === "dark"
      ? colorstyle?.textDark || "#ffffff"
      : colorstyle?.text || theme.palette.primary.main;
  };

  const getGradeColor = (grade) => {
    const gradeColors = {
      A: "#2E7D32",
      B: "#1976D2",
      C: "#FFA000",
      D: "#ED6C02",
      E: "#D32F2F",
      F: "#757575",
    };
    return gradeColors[grade] || "#757575";
  };

  return (
    <>
      {/* 
      <MyContentStats
        flashcards={flashcards}
        quizzes={quizzes}
        podcasts={podcasts}
        lessons={lessons}
        essays={essays}
        chats={chats}
        assistentChats={assistentChats}
        hpTests={hpTests}
        loading={loading}
        role={role}
      />
*/}
      <Box
        sx={{
          display: { xs: "flex", md: "flex" },
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box
          component="h2"
          sx={{
            fontSize: 22,
            width: "100%",
            textAlign: "left",
            fontWeight: "600",
            color: "text.primary",
            m: 0,
          }}
        >
          Mitt innehåll
        </Box>
      </Box>
      <Box
        sx={{
          mt: 2,
          p: 0,

          borderRadius: 4,
          bgcolor: theme.palette.mode === "dark" ? "#283947" : "white",
          backgroundImage: "none",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
          animation: "fadeIn 0.4s ease forwards",
          "@keyframes fadeIn": {
            "0%": { opacity: 0, transform: "translateY(10px)" },
            "100%": { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            background: theme.palette.mode === "dark" ? "#22303c" : "#f9fafb",

            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: 2,
            }}
          >
            {isMobile ? (
              // Mobile version with scrollable tabs
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    borderColor: "transparent",
                    overflowX: "auto",
                    overflowY: "hidden",
                    width: "100%",
                    flexWrap: "nowrap",
                    WebkitOverflowScrolling: "touch",
                    "&::-webkit-scrollbar": { display: "none" },
                    msOverflowStyle: "none",
                    scrollbarWidth: "none",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      bottom: 0,
                      width: "60px",
                      background:
                        "linear-gradient(to right, transparent, rgba(255,255,255,0.9))",
                      pointerEvents: "none",
                      zIndex: 100,
                      borderTopRightRadius: "0.75rem",
                      borderBottomRightRadius: "0.75rem",
                    }}
                  />
                  <TabButton
                    active={activeFilter === "all"}
                    onClick={() => {
                      setActiveFilter("all");
                      setCurrentPage(1);
                    }}
                    disableRipple
                    sx={{
                      flex: "0 0 auto",
                      minWidth: "100px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span>Allt</span>
                    </Box>
                  </TabButton>
                  {Object.entries(typeStyles)
                    .filter(
                      ([type]) =>
                        !(type === "lessons" && role !== "teacher") &&
                        !(type === "hpTests" && role !== "student"),
                    )
                    .map(([type, style]) => {
                      const Icon = style.icon;
                      return (
                        <TabButton
                          key={type}
                          active={activeFilter === type}
                          onClick={() => {
                            setActiveFilter(type);
                            setCurrentPage(1);
                          }}
                          disableRipple
                          sx={{
                            flex: "0 0 auto",
                            minWidth: "130px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Icon fontSize="small" />
                            <span>{getTypeName(type)}</span>
                          </Box>
                        </TabButton>
                      );
                    })}
                </Box>
              </Box>
            ) : (
              // Original desktop version - completely unchanged
              <Box
                sx={{
                  display: "flex",
                  borderColor: "transparent",
                  overflowX: "auto",
                  "&::-webkit-scrollbar": { display: "none" },
                  msOverflowStyle: "none",
                  scrollbarWidth: "none",
                }}
              >
                <TabButton
                  active={activeFilter === "all"}
                  onClick={() => {
                    setActiveFilter("all");
                    setCurrentPage(1);
                  }}
                  disableRipple
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <span>Alla</span>
                  </Box>
                </TabButton>
                {Object.entries(typeStyles)
                  .filter(
                    ([type]) =>
                      !(type === "lessons" && role !== "teacher") &&
                      !(type === "hpTests" && role !== "student"),
                  )
                  .map(([type, style]) => {
                    const Icon = style.icon;
                    return (
                      <TabButton
                        key={type}
                        active={activeFilter === type}
                        onClick={() => {
                          setActiveFilter(type);
                          setCurrentPage(1);
                        }}
                        disableRipple
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Icon fontSize="small" />
                          <span>{getTypeName(type)}</span>
                        </Box>
                      </TabButton>
                    );
                  })}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ p: 4 }}>
          {loading[activeFilter === "all" ? "all" : activeFilter] ? (
            <Box sx={{ p: 0 }}>
              <Grid container spacing={3}>
                {/* Generate multiple skeleton cards based on itemsPerPage */}
                {Array.from(new Array(itemsPerPage)).map((_, index) => (
                  <Grid item xs={12} sm={6} lg={4} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor:
                          theme.palette.mode === "dark" ? "#1e2a38" : "#fff",
                        transition: "transform 0.2s, box-shadow 0.2s",
                        height: "100%",
                      }}
                    >
                      <Box sx={{ p: 1 }}>
                        {/* Type badge and date skeleton */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1.5,
                          }}
                        >
                          <Skeleton variant="rounded" width={100} height={24} />
                          <Skeleton variant="text" width={80} />
                        </Box>

                        {/* Title skeleton */}
                        <Skeleton
                          variant="text"
                          sx={{ fontSize: "1rem", mb: 1 }}
                        />
                        <Skeleton
                          variant="text"
                          sx={{ fontSize: "1rem", width: "70%" }}
                        />

                        {/* Content skeleton - different for each type */}
                        <Box sx={{ mt: 2 }}>
                          {activeFilter === "podcasts" ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Skeleton
                                variant="circular"
                                width={40}
                                height={40}
                              />
                              <Skeleton variant="text" width={20} />
                              <Skeleton
                                variant="circular"
                                width={40}
                                height={40}
                              />
                            </Box>
                          ) : activeFilter === "assistentChats" ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Skeleton
                                variant="circular"
                                width={40}
                                height={40}
                              />
                              <Skeleton variant="text" width={120} />
                            </Box>
                          ) : (
                            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                              <Skeleton
                                variant="rounded"
                                width={80}
                                height={24}
                              />
                              <Skeleton
                                variant="rounded"
                                width={80}
                                height={24}
                              />
                            </Box>
                          )}
                        </Box>

                        {/* Badge for score or additional info */}
                        {(activeFilter === "quizzes" ||
                          activeFilter === "flashcards" ||
                          activeFilter === "assessments") && (
                          <Box
                            sx={{ position: "absolute", bottom: 16, right: 16 }}
                          >
                            <Skeleton
                              variant="circular"
                              width={30}
                              height={30}
                            />
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <>
              {filteredItems.length > 0 ? (
                <>
                  <Grid container spacing={3}>
                    {paginatedItems.map((item) => {
                      const colorStyle = typeStyles[item.type];
                      const Icon = colorStyle?.icon;

                      return (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          lg={4}
                          key={`${item.type}-${item.id}`}
                        >
                          <ContentCard
                            onClick={() => handleItemClick(item)}
                            elevation={0}
                            colorstyle={colorStyle}
                          >
                            <Box sx={{ p: 1 }}>
                              <CardContent>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "flex-start",
                                    mb: 1.5,
                                  }}
                                >
                                  <TypeBadge colorstyle={colorStyle}>
                                    <Box className="iconWrapper">
                                      {Icon && (
                                        <Icon
                                          className="iconWrapper-icon"
                                          fontSize="small"
                                        />
                                      )}
                                    </Box>
                                    <span>{getTypeName(item.type)}</span>
                                  </TypeBadge>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {item.date}
                                  </Typography>
                                </Box>

                                <Typography
                                  variant="h6"
                                  sx={{
                                    mb: 2,
                                    lineHeight: 1.3,
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                  }}
                                >
                                  {item.title}
                                </Typography>
                                {item.type === "chats" &&
                                  item.lastMessageContent && (
                                    <Box
                                      sx={{
                                        mt: 2,
                                      }}
                                    >
                                      <InfoBadge>
                                        {item.lastMessageContent.substring(
                                          0,
                                          40,
                                        )}
                                        {item.lastMessageContent.length > 30 &&
                                          "..."}
                                      </InfoBadge>
                                    </Box>
                                  )}
                                {item.type === "assessments" && item.grade && (
                                  <Box
                                    sx={{
                                      mt: 2,
                                    }}
                                  >
                                    <InfoBadge>{item.category}</InfoBadge>
                                    <Box
                                      sx={{
                                        bgcolor: getGradeColor(item.grade),
                                        color: "white",
                                        width: 28,
                                        height: 28,
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        opacity: "0.9",
                                        position: "absolute",
                                        bottom: 23,
                                        right: 23,
                                      }}
                                    >
                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: "bold",
                                          fontSize: "0.9rem",
                                        }}
                                      >
                                        {item.grade}
                                      </Typography>
                                    </Box>
                                  </Box>
                                )}

                                {item.type === "assistentChats" && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-start",
                                      gap: 2,
                                    }}
                                  >
                                    <Avatar
                                      src={item.imageUrl}
                                      alt="Speaker 1"
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        transition: "transform 0.3s ease",
                                        boxShadow:
                                          "0px 4px 10px rgba(0, 0, 0, 0.1)",
                                      }}
                                      className="avatarTransition"
                                    />
                                    {item.lastMessageContent && (
                                      <InfoBadge>
                                        {item.lastMessageContent.substring(
                                          0,
                                          40,
                                        )}
                                        {item.lastMessageContent.length > 40 &&
                                          "..."}
                                      </InfoBadge>
                                    )}
                                  </Box>
                                )}

                                {item.type === "hpTests" && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1,
                                    }}
                                  >
                                    {/* Top row with status and questions count */}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      {/* Status indicator */}
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        {item.testInProgress ? (
                                          <PlayArrowIcon
                                            fontSize="small"
                                            color="primary"
                                            sx={{ mr: 0.5 }}
                                          />
                                        ) : (
                                          <CheckCircleIcon
                                            fontSize="small"
                                            color="success"
                                            sx={{ mr: 0.5 }}
                                          />
                                        )}
                                        <Typography
                                          variant="body2"
                                          sx={{ color: "text.secondary" }}
                                        >
                                          {item.testInProgress
                                            ? "Pågående"
                                            : "Slutfört"}
                                        </Typography>
                                      </Box>

                                      {/* Questions count */}
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <QuestionAnswerIcon
                                          fontSize="small"
                                          sx={{
                                            mr: 0.5,
                                            color: "text.secondary",
                                          }}
                                        />
                                        <Typography
                                          variant="body2"
                                          sx={{ color: "text.secondary" }}
                                        >
                                          {`${item.answeredQuestionsCount} / ${item.totalQuestions}`}
                                        </Typography>
                                      </Box>
                                    </Box>

                                    {/* Bottom row with correct answers and time (only when test is completed) */}
                                    {!item.testInProgress && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                        }}
                                      >
                                        {/* Correct answers count */}
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <CheckIcon
                                            fontSize="small"
                                            sx={{
                                              mr: 0.5,
                                              color: "text.secondary",
                                            }}
                                          />
                                          <Typography
                                            variant="body2"
                                            sx={{ color: "text.secondary" }}
                                          >
                                            {`${item.results.filter((r) => r.isCorrect).length}/${item.results.length}`}
                                          </Typography>
                                        </Box>

                                        {/* Time used */}
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <AccessTimeIcon
                                            fontSize="small"
                                            sx={{
                                              mr: 0.5,
                                              color: "text.secondary",
                                            }}
                                          />
                                          <Typography
                                            variant="body2"
                                            sx={{ color: "text.secondary" }}
                                          >
                                            {`${Math.floor((55 * 60 - item.timeLeft) / 60)}:${
                                              (55 * 60 - item.timeLeft) % 60 <
                                              10
                                                ? "0"
                                                : ""
                                            }${(55 * 60 - item.timeLeft) % 60}`}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )}
                                  </Box>
                                )}

                                {item.type === "podcasts" && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "flex-start",

                                      gap: 2,
                                    }}
                                  >
                                    <Avatar
                                      src={getSpeakerAvatar(item.speaker1)}
                                      alt="Speaker 1"
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        transition: "transform 0.3s ease",
                                        boxShadow:
                                          "0px 4px 10px rgba(0, 0, 0, 0.1)",
                                      }}
                                      className="avatarTransition"
                                    />
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 600, fontSize: "1rem" }}
                                    >
                                      &
                                    </Typography>
                                    <Avatar
                                      src={getSpeakerAvatar(item.speaker2)}
                                      alt="Speaker 2"
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        transition: "transform 0.3s ease",
                                        boxShadow:
                                          "0px 4px 10px rgba(0, 0, 0, 0.1)",
                                      }}
                                      className="avatarTransition"
                                    />
                                  </Box>
                                )}

                                {item.type === "quizzes" &&
                                  item.results?.some(
                                    (result) =>
                                      result.selectedOption ||
                                      result.type === "exam",
                                  ) && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        bottom: 23,
                                        right: 23,
                                      }}
                                    >
                                      <QuizResultsTooltip
                                        quiz={item}
                                        theme={theme}
                                      />
                                    </Box>
                                  )}

                                {item.type === "flashcards" &&
                                  item.flashcards?.some(
                                    (card) => card.correct,
                                  ) && (
                                    <Box
                                      sx={{
                                        position: "absolute",
                                        bottom: 23,
                                        right: 23,
                                      }}
                                    >
                                      <FlashcardResultsTooltip
                                        collection={item}
                                        theme={theme}
                                        compact={true}
                                      />
                                    </Box>
                                  )}

                                {item.type === "lessons" && (
                                  <InfoBadge>
                                    {lessonMapping(item.lessonType)}
                                  </InfoBadge>
                                )}

                                <Box
                                  sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 1,
                                  }}
                                >
                                  {item.cards && (
                                    <InfoBadge>{item.cards} kort</InfoBadge>
                                  )}
                                  {item.questions && (
                                    <InfoBadge>
                                      {item.questions} frågor
                                    </InfoBadge>
                                  )}
                                  {item.duration && (
                                    <InfoBadge>{item.duration}</InfoBadge>
                                  )}
                                </Box>
                              </CardContent>
                            </Box>
                          </ContentCard>
                        </Grid>
                      );
                    })}
                  </Grid>
                  {filteredItems.length > itemsPerPage && (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                    >
                      <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                      />
                    </Box>
                  )}

                  {activeFilter !== "all" && (
                    <>
                      <Divider
                        sx={{ mt: 4, color: "text.secondary", opacity: 0.5 }}
                      />
                      <Box
                        sx={{
                          width: { xs: "100%", md: "33%" },
                          margin: "0 auto",

                          mt: 4,
                        }}
                      >
                        <Grid container spacing={3}>
                          <Grid
                            item
                            xs={12}
                            sm={
                              activeFilter === "assistentChats" ||
                              activeFilter === "chats"
                                ? 12
                                : 6
                            }
                          >
                            <ActionBox
                              elevation={0}
                              onClick={handleCreateNew}
                              colorstyle={typeStyles[activeFilter]}
                            >
                              <AddIcon
                                className="plusIcon"
                                sx={{
                                  fontSize: 30, // Reduced from 36
                                  color: getIconColor(
                                    theme,
                                    typeStyles[activeFilter],
                                  ),
                                  transition: "transform 0.3s ease",
                                }}
                              />
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                    textAlign: "center",
                                  }}
                                >
                                  {handleCreateNewButtonText(activeFilter)}
                                </Typography>
                              </Box>
                            </ActionBox>
                          </Grid>
                          {activeFilter !== "assistentChats" &&
                            activeFilter !== "chats" &&
                            activeFilter !== "hpTests" && (
                              <Grid item xs={12} sm={6}>
                                <ActionBox
                                  elevation={0}
                                  onClick={handleShowAll}
                                  colorstyle={typeStyles[activeFilter]}
                                >
                                  <ViewAgendaIcon
                                    sx={{
                                      fontSize: 30, // Reduced from 36
                                      color: getIconColor(
                                        theme,
                                        typeStyles[activeFilter],
                                      ),
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.95rem",
                                      }}
                                    >
                                      Visa fler
                                    </Typography>
                                  </Box>
                                </ActionBox>
                              </Grid>
                            )}
                        </Grid>
                      </Box>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Box sx={{ textAlign: "center" }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        p: 3,
                        backgroundColor: alpha(
                          theme.palette.background.paper,
                          0.6,
                        ),
                        borderRadius: 2,
                        textAlign: "center",
                        mb: 2,
                      }}
                    >
                      <Typography color="text.secondary">
                        {activeFilter === "all"
                          ? "Du har inte genererat något innehåll ännu. Välj vad du vill skapa ovan."
                          : "Du har inte genererat något ännu. Klicka på knappen nedan."}
                      </Typography>
                    </Box>

                    {activeFilter !== "all" && (
                      <Box
                        sx={{
                          width: { xs: "100%", md: "33%" },
                          margin: "0 auto",
                          mb: 6,
                        }}
                      >
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={12}>
                            <ActionBox
                              elevation={0}
                              onClick={handleCreateNew}
                              colorstyle={typeStyles[activeFilter]}
                            >
                              <AddIcon
                                className="plusIcon"
                                sx={{
                                  fontSize: 30,
                                  color: getIconColor(
                                    theme,
                                    typeStyles[activeFilter],
                                  ),
                                  transition: "transform 0.3s ease",
                                }}
                              />
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                  }}
                                >
                                  {handleCreateNewButtonText(activeFilter)}
                                </Typography>
                              </Box>
                            </ActionBox>
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </Box>

                  {/* Show action boxes at the bottom for non-empty tabs too */}
                  {activeFilter !== "all" && filteredItems.length > 0 && (
                    <>
                      <Divider
                        sx={{ mt: 4, color: "text.secondary", opacity: 0.5 }}
                      />
                      <Box
                        sx={{
                          width: { xs: "100%", md: "33%" },
                          margin: "0 auto",
                          mt: 4,
                        }}
                      >
                        <Grid container spacing={3}>
                          <Grid
                            item
                            xs={12}
                            sm={
                              activeFilter === "assistentChats" ||
                              activeFilter === "chats"
                                ? 12
                                : 6
                            }
                          >
                            <ActionBox
                              elevation={0}
                              onClick={handleCreateNew}
                              colorstyle={typeStyles[activeFilter]}
                            >
                              <AddIcon
                                className="plusIcon"
                                sx={{
                                  fontSize: 30,
                                  color: getIconColor(
                                    theme,
                                    typeStyles[activeFilter],
                                  ),
                                  transition: "transform 0.3s ease",
                                }}
                              />
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                  }}
                                >
                                  {handleCreateNewButtonText(activeFilter)}
                                </Typography>
                              </Box>
                            </ActionBox>
                          </Grid>
                          {activeFilter !== "assistentChats" &&
                            activeFilter !== "chats" && (
                              <Grid item xs={12} sm={6}>
                                <ActionBox
                                  elevation={0}
                                  onClick={handleShowAll}
                                  colorstyle={typeStyles[activeFilter]}
                                >
                                  <ViewAgendaIcon
                                    sx={{
                                      fontSize: 30,
                                      color: getIconColor(
                                        theme,
                                        typeStyles[activeFilter],
                                      ),
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="h6"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.95rem",
                                      }}
                                    >
                                      Visa fler
                                    </Typography>
                                  </Box>
                                </ActionBox>
                              </Grid>
                            )}
                        </Grid>
                      </Box>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default MyContent;
